import {
	ChevronRightIcon,
	ChevronDownIcon,
	DownloadIcon,
} from "@chakra-ui/icons";
import { Flex, Box, Badge } from "@chakra-ui/layout";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	MenuGroup,
	Card,
	CardBody,
	Text,
	Button,
	useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import CardTitle from "../components/CardTitle";
import CopyOnClick from "../components/CopyOnClick";
import QrCodeIcon from "../components/icons/QrCodeIcon";
import TicketIcon from "../components/icons/TicketIcon";
import OrderStatusBadge from "../components/OrderStatusBadge";
import PageTitle from "../components/PageTitle";
import { useCenter } from "../hooks/centers.hooks";
import {
	useCommand,
	useCommandSupplyOrder,
	useCommandTickets,
} from "../hooks/commands.hooks";
import { useConnectedUser } from "../hooks/user.hooks";
import { fetchFileContent } from "../mutations/file.mutations";
import type { SavedFile } from "../types/files.types";
import FilePreview from "../components/FilePreview";
import OrdersDetailTicket from "./OrdersDetailTicket";
import FileIconPdf from "../components/icons/files/FileIconPdf";
import { CommandStatus } from "../types/command.types";
import {
	changeCommandStatus,
	updateCommand,
} from "../mutations/command.mutations";
import { format } from "date-fns";
import HaltIcon from "../components/icons/HaltIcon";
import TrashIcon from "../components/icons/TrashIcon";
import HaltOrderModal from "../components/modals/HaltOrderModal";
import DeleteOrderModal from "../components/modals/DeleteOrderModal";
import { type MutateErrors, mutateOnError } from "../lib/generic";
import { fPhoneNumber } from "../lib/format";
import { printFileById } from "../hooks/printers.hooks";
import { useSimpleToast } from "../hooks/toast.hooks";
import { getCommandMaterialContext } from "../utils/getCommandMaterialContext";
import { getTicketCommandId } from "../services/command.service";

const OrdersSupplyDetailPage = () => {
	const { id } = useParams();
	const { data: command } = useCommand(id ?? null);
	const { data: supplyOrder } = useCommandSupplyOrder(id ?? null);
	const { tickets } = useCommandTickets(id);
	const total = useMemo(() => {
		if (Array.isArray(supplyOrder)) {
			return supplyOrder.reduce(
				(total, so) => total + so.supply.price * so.quantity,
				0,
			);
		}
		return undefined;
	}, [supplyOrder]);
	const {
		isOpen: haltModalIsOpen,
		onOpen: haltModalOnOpen,
		onClose: haltModalOnClose,
	} = useDisclosure();
	const {
		isOpen: deleteModalIsOpen,
		onOpen: deleteModalOnOpen,
		onClose: deleteModalOnClose,
	} = useDisclosure();

	const { data: center } = useCenter(command?.centerId);
	const navigate = useNavigate();
	const user = useConnectedUser();
	const selectedFile = useRef<SavedFile | null>(null);
	const selectedFilename = useRef<string>("");
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: ArrayBuffer) => {
			if (selectedFile?.current !== null) {
				const blob = new Blob([content]);
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = selectedFilename.current;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				selectedFile.current = null;
			}
		},
		onError: (err: MutateErrors) => {
			mutateOnError(err);
			selectedFile.current = null;
		},
	});
	const queryClient = useQueryClient();
	const { mutate: mutateCommandUpdate } = useMutation(updateCommand, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const toast = useSimpleToast();
	const handleChangeStatus = (status: CommandStatus) => {
		if (
			status === CommandStatus.INJECTING &&
			command &&
			getCommandMaterialContext(command).canInjectSilicone === false
		) {
			toast.error(
				"Command",
				"Seules les commandes avec au moins une oreille en silicone peuvent être en injection.",
			);
			return;
		}
		mutateCommandUpdate(
			{
				id: command?.id,
				status,
			},
			{
				onSuccess: () => {
					toast.success("Command", "Statut mis à jour avec succès");
				},
				onError: () => {
					toast.error("Command", "Erreur lors de la mise à jour du statut");
				},
			},
		);
	};
	const { mutate: mutateCommandStatus } = useMutation(changeCommandStatus, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const handleHalt = () => {
		if (user?.isSuperAdmin) {
			if (id) {
				mutateCommandStatus({
					id: Number.parseInt(id),
					status: "HALTED",
					reason: "",
					explanation: "",
				});
			}
		} else {
			haltModalOnOpen();
		}
	};
	if (command === null || command === undefined) {
		return <>---</>;
	}
	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/orders"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Commandes
					</BreadcrumbLink>
				</BreadcrumbItem>

				{center && (
					<BreadcrumbItem>
						<BreadcrumbLink
							href={`/contacts/centers/${center.id}`}
							color="gray.600"
							fontWeight={600}
							fontSize="14px"
						>
							{center.name}
						</BreadcrumbLink>
					</BreadcrumbItem>
				)}

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						{id}
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			{/* HEADER */}
			<Flex justifyContent="space-between">
				<Flex alignItems={"center"}>
					<CopyOnClick content={command.exposedId}>
						<PageTitle>{command.exposedId}</PageTitle>
					</CopyOnClick>
					<OrderStatusBadge
						halted={command.haltedAt != null}
						status={command.status}
						ml={3}
					></OrderStatusBadge>
				</Flex>
				<Flex gap={3}>
					<Menu>
						{user?.isSuperAdmin && (
							<MenuButton
								as={Button}
								bg="white"
								rightIcon={<ChevronDownIcon />}
							>
								Changer le statut
							</MenuButton>
						)}
						<MenuList>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_RECEPTION)
								}
							>
								En attente de réception
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.RECEIVED)}
							>
								Réceptionné
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_MODELING)
								}
							>
								En attente de modélisation
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.MODELING)}
							>
								En modélisation
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_PRINTING)
								}
							>
								Prête pour impression
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.PRINTING)}
							>
								En impression
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.PRINTED)}
							>
								Imprimé
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_LAB)
								}
							>
								À traiter labo
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.INJECTING)}
							>
								En injection silicone
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.POLISHING)}
							>
								En finition
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.QUALITY_CONTROL)
								}
							>
								Contrôle qualité
							</MenuItem>
							<MenuItem onClick={() => handleChangeStatus(CommandStatus.READY)}>
								Prête
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.READY_FOR_DELIVERY)
								}
							>
								Prêt à expédier
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.PARCEL_PREPARED)
								}
							>
								Colis préparé
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.IN_DELIVERY)}
							>
								Expédiée
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.DELIVERED)}
							>
								Livré
							</MenuItem>
						</MenuList>
					</Menu>
					<Menu>
						<MenuButton
							as={Button}
							bg="brand.600"
							color="white"
							rightIcon={<ChevronDownIcon />}
						>
							Actions
						</MenuButton>
						<MenuList>
							<MenuGroup title="Gestion de la commande">
								<MenuItem
									onClick={() =>
										navigate(
											`/tickets/new?commandId=${getTicketCommandId(command)}`,
										)
									}
								>
									<TicketIcon mr={3} />
									Créer un ticket
								</MenuItem>
								{command.haltedAt === null && (
									<MenuItem onClick={() => handleHalt()}>
										<HaltIcon mr={3}></HaltIcon>
										{user?.isSuperAdmin
											? "Mettre en pause"
											: "Demande de mise en pause"}
									</MenuItem>
								)}
								<MenuItem onClick={() => deleteModalOnOpen()} color="#B91C1C">
									<TrashIcon mr={3}></TrashIcon>
									{user?.isSuperAdmin
										? "Annuler la commande"
										: "Demande d'annulation de la commande"}
								</MenuItem>
							</MenuGroup>
							<MenuGroup title="Documents">
								<MenuItem
									onClick={() => {
										if (
											command.purchaseOrderFileId &&
											command.purchaseOrderFile
										) {
											selectedFile.current = command.purchaseOrderFile;
											selectedFilename.current = `Commande-${command?.exposedId}.pdf`;
											downloadFileContent(command.purchaseOrderFileId);
										}
									}}
								>
									<DownloadIcon mr={3} />
									Télécharger le bon de commande
								</MenuItem>
								{user?.isSuperAdmin && (
									<MenuItem
										onClick={() =>
											printFileById(
												command.labelFileId!,
												"Imprimante Etiquettes",
												"test",
												{},
											)
										}
									>
										<QrCodeIcon mr={3} />
										Imprimer l'étiquette
									</MenuItem>
								)}
							</MenuGroup>
						</MenuList>
					</Menu>
				</Flex>
			</Flex>
			{command.discount !== null && command.discount !== 0 ? (
				<Flex mb={4} color="green">
					Remise de {command.discount}%
				</Flex>
			) : (
				""
			)}
			{/* BODY */}
			<Flex w="100%" gap={3} mt={4}>
				<Flex gap={4} w="100%">
					<Flex flexGrow={2} w="100%" direction="column" gap={4}>
						<Card w="100%" h="fit-content">
							<CardTitle backgroundColor="gray.50">
								<Text
									lineHeight="27px"
									fontSize="18px"
									fontWeight="600"
									color="black"
								>
									Détails de la commande
								</Text>
							</CardTitle>
							<CardBody>
								<Flex direction="column">
									{Array.isArray(supplyOrder) &&
										supplyOrder.map((so) => (
											<Flex key={so.supply.id}>
												<Box
													w="100%"
													mb={4}
													borderBottom="solid 1px"
													borderColor="gray.100"
													pb={2}
												>
													<Flex
														h="100%"
														justifyContent={"space-between"}
														alignItems={"center"}
													>
														<Flex>
															{so.supply.fileId && (
																<FilePreview
																	id={so.supply.fileId}
																	mr={4}
																	w="64px"
																	h="64px"
																></FilePreview>
															)}
															<Flex direction="column">
																<Text
																	lineHeight="21px"
																	fontSize="14px"
																	fontWeight="500"
																	color="gray.900"
																>
																	{so.supply.label}
																</Text>
																<Text
																	lineHeight="21px"
																	fontSize="14px"
																	fontWeight="400"
																	color="gray.500"
																>
																	{so.supply.label}
																</Text>
															</Flex>
														</Flex>
														<Flex
															justifyContent="flex-end"
															alignItems="baseline"
															gap={4}
														>
															<Text
																lineHeight="21px"
																fontSize="14px"
																fontWeight="500"
																color="gray.500"
															>
																{(so.supply.price / 100)
																	.toString()
																	.replace(".", ",")}{" "}
																€ x {so.quantity}
															</Text>
															<Text
																lineHeight="18px"
																fontSize="12px"
																fontWeight="500"
																color="gray.900"
															>
																{((so.supply.price / 100) * so.quantity)
																	.toString()
																	.replace(".", ",")}{" "}
																€
															</Text>
														</Flex>
													</Flex>
												</Box>
											</Flex>
										))}

									<Flex justifyContent="end" alignItems="baseline" gap={4}>
										<Text
											lineHeight="18px"
											fontSize="12px"
											fontWeight="500"
											color="gray.500"
										>
											Total
										</Text>
										<Text
											lineHeight="36px"
											fontSize="24px"
											fontWeight="600"
											color="gray.900"
										>
											{total === undefined
												? "---"
												: (total / 100).toString().replace(".", ",")}
											€
										</Text>
									</Flex>
								</Flex>
							</CardBody>
						</Card>

						<Card>
							<CardTitle>
								<Flex>
									<Box fontWeight={600} fontSize="18px">
										Documents
									</Box>
								</Flex>
							</CardTitle>

							<CardBody>
								{[
									...(Array.isArray(command.draft?.additionalFiles)
										? command.draft.additionalFiles.map((file) => ({
												file: file,
												filename: file?.fileName,
											}))
										: []),
									{
										file: command.purchaseOrderFile,
										filename: `${command.exposedId}.pdf`,
									},
									{
										file: command.purchaseOrder?.file,
										filename: command.purchaseOrder?.createdAt
											? `BL${format(
													command.purchaseOrder?.createdAt,
													"yyyy",
												)}${command.purchaseOrder?.id}.pdf`
											: "Bon de livraison.pdf",
									},
									{
										file: command.purchaseOrder?.shippingLabelFile,
										filename:
											command.purchaseOrder?.shippingLabelFile?.fileName,
									},
									{
										file: command.labelFile,
										filename: command.labelFile?.fileName,
									},
								]
									.filter(
										(file) => file.file !== undefined && file.file !== null,
									)
									.map((file, index) => (
										<Flex
											key={file ? file?.file?.id : index}
											justify="space-between"
											onClick={() => {
												selectedFile.current = file.file ?? null;
												selectedFilename.current = file?.filename ?? "";
												downloadFileContent(file?.file?.id);
											}}
										>
											<Flex gap={4}>
												<FileIconPdf color="brand.700" w="24px" h="24px" />
												<Text>{file?.file?.fileName}</Text>
											</Flex>
											<DownloadIcon />
										</Flex>
									))}
							</CardBody>
						</Card>

						{/* <Card>
              <CardTitle>
                <Flex>
                  <Box fontWeight={600} fontSize="18px">
                    Documents
                  </Box>
                </Flex>
              </CardTitle>

              <CardBody>
                {[ command.purchaseOrderFile ].filter((file) => file !== undefined && file !== null)
                  .map((file, index) => (
                    <Flex
                      key={file ? file.id : index}
                      justify="space-between"
                      onClick={() => {
                        selectedFile.current = file;
                        downloadFileContent(file.id);
                      }}
                    >
                      <Flex gap={4}>
                        <FileIconPdf color="brand.700" w="24px" h="24px" />
                        <Text>{file?.fileName}</Text>
                      </Flex>
                      <DownloadIcon />
                    </Flex>
                  ))}
              </CardBody>
            </Card> */}
					</Flex>
					<Flex flexGrow={1} w="100%" direction="column" gap={4}>
						<Card w="100%">
							<CardTitle>
								<Text
									lineHeight="27px"
									fontSize="18px"
									fontWeight="600"
									color="black"
								>
									{center?.name}
								</Text>
							</CardTitle>
							<CardBody>
								<Box>
									<Text
										lineHeight="27px"
										fontSize="18px"
										fontWeight="600"
										color="gray.700"
									>
										Adresse de facturation
									</Text>
									{center && (
										<>
											<Text>{center.enterprise?.name}</Text>
											<Text>
												{center.enterprise?.address?.streetNumber}{" "}
												{center.enterprise?.address?.streetName},
											</Text>
											<Text>
												{center.enterprise?.address?.postalCode}{" "}
												{center.enterprise?.address?.city}
											</Text>
										</>
									)}
								</Box>
								<Box mt={4}>
									<Text
										lineHeight="27px"
										fontSize="18px"
										fontWeight="600"
										color="gray.700"
									>
										Adresse de livraison
									</Text>
									{center && (
										<>
											<Text>{center.name}</Text>
											<Text>
												{center.shippingAddress?.streetNumber}{" "}
												{center.shippingAddress?.streetName},
											</Text>
											<Text>
												{center.shippingAddress?.postalCode}{" "}
												{center.shippingAddress?.city}
											</Text>
											<Text>
												{fPhoneNumber(command.center?.phoneNumber ?? "")}
											</Text>
										</>
									)}
								</Box>
							</CardBody>
						</Card>

						{Array.isArray(tickets) && tickets.length !== 0 && (
							<Card w="100%">
								<CardTitle>
									<Text
										lineHeight="27px"
										fontSize="18px"
										fontWeight="600"
										color="black"
									>
										Tickets
										<Badge
											variant="subtle"
											borderRadius="xl"
											ml={2}
											px="10px"
											py={"3px"}
										>
											{Array.isArray(tickets) && tickets.length}
										</Badge>
									</Text>
								</CardTitle>
								<CardBody>
									<OrdersDetailTicket id={id}></OrdersDetailTicket>
								</CardBody>
							</Card>
						)}
					</Flex>
				</Flex>
			</Flex>
			<HaltOrderModal
				orderId={command.id}
				isOpen={haltModalIsOpen}
				onOpen={haltModalOnOpen}
				onClose={haltModalOnClose}
			></HaltOrderModal>
			<DeleteOrderModal
				orderId={command.id}
				isOpen={deleteModalIsOpen}
				onOpen={deleteModalOnOpen}
				onClose={deleteModalOnClose}
			></DeleteOrderModal>
		</>
	);
};

export default OrdersSupplyDetailPage;
